// ════════════════════════════════════════════════════════════════════════════
//  ManDana — how big a sheet may be, and how big it opens
//  ---------------------------------------------------------------------------
//  Until now a compose sheet had no size. Its root was `position: absolute;
//  inset: 0`, so it filled whatever host it was mounted in, and a single
//  boolean picked between two dress styles. The consequence on a laptop is that
//  writing one line takes the whole screen — and takes it away from the thing
//  you were looking at when you decided to write.
//
//  This module is the ladder: an ordered set of sizes, which ones a given
//  screen offers, and which one a sheet opens on when nobody has said
//  otherwise. It is a pure function of what `useMdLayout()` has already
//  measured, which is what lets the entire table be proved without a device, a
//  render, or a DOM (tests/sheet-ladder.test.js).
//
//  ── WHY A SHAPE, AND NOT A WIDTH ──────────────────────────────────────────
//  `useMdLayout` is already the one answer to "what layout are we in", and it
//  knows two things a width cannot: it promotes a tablet turned SIDEWAYS into
//  `desktop` whatever its width, and `fine` tells a mouse from a finger. Both
//  matter here. An iPad at 1180 has room for a popup and no way to dismiss one
//  comfortably; a 1180 laptop has both. Re-deriving any of that from `w` would
//  create a second, quietly disagreeing answer.
//
//  ── THE FIFTH SHAPE ───────────────────────────────────────────────────────
//  The spec names four: desktop, iPad upright, iPad sideways, mobile. There is
//  a fifth. A big iPhone turned sideways is 852×393 — wider than an upright
//  iPad, and far too short to carry anything but a full-screen sheet. It lands
//  in the tablet width band with no canvas, and a table with no row for it
//  would hand the sheet `undefined` and draw nothing. So `mdDeviceShape` is
//  total, and its last case is a fallthrough rather than a test.
//
//  The shape names describe the VIEWPORT, not the hardware: a desktop browser
//  window dragged narrow really is `tablet-tall`, and a short wide one really
//  is `phone-wide`. That is not a mislabel, it is the point — the ladder is a
//  statement about how much room there is, and the sheet should behave the same
//  way in the same amount of room however you arrived at it.
//
//  ── A RUNG STILL HAS TO BE RESOLVED, EVEN THOUGH NOTHING IS REMEMBERED ────
//  A sheet opens on its shape's default every time (Marwan, 2026-08-21) — the
//  memory CS-4 gave it turned a single choice to spread out into every record
//  afterwards taking the whole screen. But `mdSheetSizeResolve` did not go with
//  it, because a rung can still stop existing UNDER AN OPEN SHEET: rotate the
//  iPad, drag the window narrow, and the rung the sheet is standing on may not
//  be on the new ladder. It is the one door a rung comes through, and anything
//  off the current ladder falls back to that shape's default. Silently — a
//  person who rotates their iPad has not made a mistake and should not be told
//  about one. It also lands a `twoThirds` written by an older build.
//
//  ── ADDING A RUNG LATER ───────────────────────────────────────────────────
//  §1.4 (a dock in the main panel) and §1.5 (a shelf) are parked, not refused.
//  Adding one is two edits and no new logic: a name in MD_SHEET_RUNGS at the
//  position it belongs in the growing order, and the rows of LADDERS that
//  offer it. `initial` is written out per row rather than derived from
//  `sizes[0]` precisely so a rung can be added at the small end without
//  silently changing what every screen opens on.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  /* Every rung there is, smallest first. The order is the ladder's arithmetic:
     stepping, and the "are these in growing order" check, both read it.

     TWO RUNGS, NOT THREE (Marwan, 2026-08-21). `twoThirds` is retired. A sheet
     is either laid OVER your work or it IS the work for as long as it is open,
     and the middle rung was a third answer to a question that has two. It also
     cost the ladder its one sentence: a popup floats over the board, full takes
     the screen — and in between sat a rectangle that was neither, that no
     screen under 1280 could offer without shrinking (see hasRoomFor's note),
     and that had to be explained every time it appeared in a menu. A stored
     `twoThirds` from before now falls through mdSheetSizeResolve to `popup`,
     which is where the retired rung's people wanted to be anyway. */
  const MD_SHEET_RUNGS = ['popup', 'full'];

  type Ladder = { sizes: string[]; initial: string };

  /* One row per shape. A table rather than a chain of conditionals because this
     IS the spec's table, and it should stay readable next to it. */
  /* Named, so it can also be the fallback below without indexing the table for
     it — under noUncheckedIndexedAccess every lookup is `Ladder | undefined`,
     and a fallback that itself needs a fallback is not one. */
  const FULL_ONLY: Ladder = { sizes: ['full'], initial: 'full' };

  const LADDERS: Record<string, Ladder> = {
    // Room for a popup, room beside it, and a mouse to dismiss it with.
    desktop: { sizes: ['popup', 'full'], initial: 'popup' },
    // An upright iPad has the height for a popup too. Its row is identical to
    // the desktop's now that the middle rung is gone, and it stays a row of its
    // own because this table is the spec's list of SHAPES — a shape that has
    // stopped differing has not stopped existing, and §1.4's dock will land on
    // exactly one of these two.
    'tablet-tall': { sizes: ['popup', 'full'], initial: 'popup' },
    // An iPad held sideways is all canvas; a sheet on it is the thing you are
    // doing, so it is the whole screen.
    'tablet-wide': FULL_ONLY,
    phone: FULL_ONLY,
    'phone-wide': FULL_ONLY,
  };

  /** Which of the five shapes a measured layout is. Total. */
  function mdDeviceShape(layout: any): string {
    const L = layout || {};
    // `desktop` here already includes every tablet turned sideways — that
    // promotion is md-layout's, and `fine` is what splits the two apart.
    if (L.mode === 'desktop') return L.fine ? 'desktop' : 'tablet-wide';
    if (L.overlay) return 'tablet-tall';
    if (L.mode === 'phone') return 'phone';
    // Everything left is wide enough to miss the phone band and too short for a
    // canvas: the big iPhones sideways, and a short desktop window.
    return 'phone-wide';
  }

  /** The rungs this screen offers, and the one a sheet opens on. */
  function mdSheetSizes(layout: any): Ladder {
    const l = LADDERS[mdDeviceShape(layout)] || FULL_ONLY;
    // Copied, because callers hold on to this and a ladder is shared.
    const sizes = l.sizes.filter((s) => s === l.initial || hasRoomFor(s, layout));
    return { sizes, initial: l.initial };
  }

  /* A RUNG IS ONLY OFFERED WHERE IT IS ACTUALLY BIGGER THAN THE ONE BELOW IT.
     The rungs are defined by three unrelated rules — the popup by a fixed
     measure, two-thirds by a fraction of the screen floored by the day panel,
     full by the screen — and those rules cross over at widths nobody thinks to
     check. They do cross: on a 1280 laptop the trailing two-thirds is 816pt and
     the popup is 880, so "expand" would have made the sheet NARROWER.

     Fudging a constant would only move the crossing point. The honest rule is
     that a screen without the room for a rung is not offered it, which is also
     the rule an upright iPad has always followed — it simply has one more
     screen it now applies to. The ladder that survives is still ordered, still
     opens on the same rung, and still grows every time you press expand.

     `initial` is exempt: a shape must always be able to draw what it opens on,
     and every default is either the smallest rung or the only one.

     THE WIDTH TEST RELAXED FROM `>` TO `>=` WHEN twoThirds RETIRED, and the
     reason is worth keeping. Strictly-wider was aimed at the middle rung, which
     really could come out NARROWER than the popup below it. `full` never can:
     it is the whole screen less the same gutter, and the popup starts at that
     gutter or further in. What it can do is come out exactly AS WIDE — an
     upright iPad at 820 gives both rungs 776pt — while being 400pt taller.
     Growing there is real, and a rule that reads only the width would have
     silently taken the second rung away from every upright iPad. Area stays
     strict, so a rung that gains nothing at all is still not offered. */
  function hasRoomFor(size: string, layout: any): boolean {
    const i = MD_SHEET_RUNGS.indexOf(size);
    if (i <= 0) return true;
    const here = mdSheetSpan(size, layout, true);
    const below = mdSheetSpan(MD_SHEET_RUNGS[i - 1] as string, layout, true);
    return here.w >= below.w && here.h * here.w > below.h * below.w;
  }

  /** A stored rung, made safe for the ladder it is being restored into. */
  function mdSheetSizeResolve(remembered: any, layout: any): string {
    const { sizes, initial } = mdSheetSizes(layout);
    return sizes.indexOf(remembered) >= 0 ? remembered : initial;
  }

  /** One rung up (`dir` 1) or down (−1). Stops at the ends; never wraps. */
  function mdSheetStep(size: any, layout: any, dir: number): string {
    const { sizes } = mdSheetSizes(layout);
    // Resolve first: stepping from a rung this shape does not have has to start
    // from where the sheet actually is, which is what the fallback drew.
    const here = mdSheetSizeResolve(size, layout);
    const at = sizes.indexOf(here);
    const next = at + (dir > 0 ? 1 : dir < 0 ? -1 : 0);
    return sizes[next] ?? here;
  }

  // ══ THE RECTANGLE ════════════════════════════════════════════════════════
  /* The gutter is not a new number. md-databases.jsx publishes the one the
     Projects and Workshop windows already keep, and a sheet standing beside
     those windows has to keep their margin or the two read as different kinds
     of thing. Read lazily rather than at module scope: this file loads early
     enough to answer md-compose's first render, and md-databases.jsx loads much
     later — but nothing CALLS the geometry until both are up.
     tests/sheet-frame.test.js pins the two together so the fallback cannot
     quietly become a second opinion. */
  const MD_SHEET_GUTTER = 22;
  const gutter = (): number =>
    (typeof g.MD_SPACE_GUTTER === 'number' ? g.MD_SPACE_GUTTER : MD_SHEET_GUTTER);

  /* The popup's cap. MD_MEASURE — the writing column — is 780, and this is that
     plus 50 of air either side. It is deliberately a CAP and not a width: on a
     screen too narrow to spend it, the gutters decide instead and the measure
     simply narrows, which is what it does in the 420pt day panel already. */
  const MD_SHEET_POPUP_W = 880;
  /* And a ceiling on height, because a popup that is as tall as the screen is
     not a popup — the ground above and below it is what says this is over your
     work rather than instead of it. */
  const MD_SHEET_POPUP_H = 720;

  /* THE INLINE EDGES ARE LOGICAL, ALL OF THEM. `insetInlineStart` is the day
     panel's side in English and the other side in Arabic, which is the whole of
     what B6 asks for — "two thirds on the right" is a statement about the
     trailing side, not about the right. env() is physical, so the safe-area
     token is deliberately side-agnostic and the same string serves both edges. */
  /* THE BLOCK PAIR IS THE SIDE PANEL'S PAIR, and it is borrowed rather than
     restated. main moved the windows onto `--md-top-inset` / `--md-bottom-inset`
     so a split workspace's top and bottom edges line up with the day beside it
     instead of sitting 22px inside them; the sheet is the same rectangle as a
     window at full, so it follows the same tokens. sheet-frame.test.js compares
     the two function to function, which is what caught the drift here. */
  const g_top = 'var(--md-top-inset)';
  const g_bottom = 'var(--md-bottom-inset)';
  const g_inline = `max(var(--md-safe-inline), ${MD_SHEET_GUTTER}px)`;

  /* The narrowest a popup may be before it stops being somewhere you can write.
     420 is the day panel's own width — the measure the whole phone design is
     drawn at — so a popup this narrow is still a column this app knows how to
     set type in. Below it the popup would be a slot, not a sheet. */
  const MD_SHEET_POPUP_MIN = 420;

  const panelOf = (layout: any): number =>
    (layout && typeof layout.panel === 'number' ? layout.panel : 420);

  /* THE DAY IS ONLY DOCKED ON SOME SHAPES. On an upright iPad the day FLOATS
     over the leading edge of a full-width board (md-layout's `overlay`), so
     there is no strip to clear and a popup centres on the whole screen. Reading
     `panel` without this test would have reserved 420pt of nothing there. */
  const dockedPanel = (layout: any): number =>
    (!layout || layout.overlay || layout.phone ? 0 : panelOf(layout));

  /* WHETHER THIS SHAPE HAS A SECOND SIZE AT ALL, which is the whole of what
     decides how `full` is drawn — see mdSheetFrame. Read straight off the
     table rather than through mdSheetSizes(), because mdSheetSizes asks
     hasRoomFor, which asks mdSheetSpan, which asks this. The table is the
     honest source anyway: it is where the shapes are declared. */
  function laddered(layout: any): boolean {
    return (LADDERS[mdDeviceShape(layout)] || FULL_ONLY).sizes.length > 1;
  }

  /* ── DOES THIS RUNG REACH THE SCREEN'S EDGES? ────────────────────────────
     The one question the sheet's DRESS turns on, and for a long time the
     answer was simply "is the rung called full", which was true only while
     full meant `inset: 0`.

     It is not the rung's name because it never was: what the dress is really
     asking is whether there is any app left showing beside the sheet. If there
     is, the sheet is a thing laid ON the app — it wears the window's hairline
     and the window's shadow so it reads as lifted, and it dims what is behind
     so the margin you can see is a margin you can click to put it away. If
     there is not, a border is drawn against the bezel, a shadow falls on
     nothing, and a scrim covers a screen nobody can see.

     Both halves of that were wrong on a desktop the moment full grew a gutter:
     22pt of board showing all the way round a modal, undimmed and dead to the
     pointer. So the dress asks this instead, and the two answers agree by
     construction — `laddered` is the same test mdSheetFrame uses to decide
     whether to draw the gutter in the first place. */
  function mdSheetBleeds(size: any, layout: any): boolean {
    return mdSheetSizeResolve(size, layout) === 'full' && !laddered(layout);
  }

  /* THE NEAREST A POPUP MAY COME TO THE DAY, which is not the same question as
     where it stands. Every other window in this app — Projects, the Workshop, a
     database — clears the day at every size below full, and a compose sheet
     that alone lay across it would read as a mistake rather than a decision.

     THE SECOND GUTTER IS THE DAY'S, and it is not decoration. The day ends at
     `gutter + panel`; a sheet that begins exactly there begins where the day
     stops, which in a real engine put 8pt between the two — near enough to
     read as two surfaces that failed to line up rather than one laid beside the
     other. It never came up before because the old rule centred the popup in
     what was left over, so there was always air by accident. Moving it to the
     middle of the screen is precisely what let it arrive at the boundary, so
     the clearance has to say the air out loud.

     The inner `min` is the exception that keeps the rule honest. On a screen too
     narrow to clear the day AND still leave a column worth writing in, the
     writing wins and the popup moves back over the day. Protecting the spine is
     worth doing right up to the point where it costs you somewhere to write. */
  function popupClear(layout: any): number {
    const w = (layout && layout.w) || 0;
    const G = gutter();
    const day = dockedPanel(layout);
    return Math.max(G, Math.min(G + day + (day ? G : 0), w - G - MD_SHEET_POPUP_MIN));
  }

  /* How wide a popup comes out: its cap, or whatever is left once the day is
     cleared. Measured from `popupClear` rather than from where the sheet ends
     up standing, so that moving the sheet can never change its width — the two
     answers used to be one expression and it was quietly circular. */
  function popupBox(layout: any): number {
    const w = (layout && layout.w) || 0;
    return Math.min(MD_SHEET_POPUP_W, w - popupClear(layout) - gutter());
  }

  /* WHERE A POPUP BEGINS: THE MIDDLE OF THE SCREEN, PUSHED CLEAR OF THE DAY
     (Marwan, 2026-08-23).

     It used to centre on the BOARD — the strip left over once the day was
     cleared — and that reads wrong for a reason worth writing down: nobody can
     see the region it was centring in. The eye measures a floating thing
     against the edges it can see, which are the screen's, so on a wide display
     the sheet sat visibly right of the middle with the day it was politely
     avoiding nowhere near it and a third of the window left over beside it.

     So the middle of the screen is what a popup wants, and the day is the one
     thing allowed to refuse it. Where they disagree the day wins and the popup
     stands against the day's edge — as near the middle as it may come, and
     never on it. Above about 1764pt the two stop disagreeing and the popup is
     simply centred, which is where this started. */
  function mdSheetPopupStart(layout: any): number {
    const w = (layout && layout.w) || 0;
    return Math.max(popupClear(layout), (w - popupBox(layout)) / 2);
  }

  /** The rectangle, as numbers. `raw` skips the ladder (see hasRoomFor). */
  function mdSheetSpan(size: any, layout: any, raw?: boolean): { w: number; h: number } {
    const L = layout || {};
    const H = typeof L.h === 'number' ? L.h : 0;
    const G = gutter();
    const rung = raw ? size : mdSheetSizeResolve(size, layout);
    if (rung === 'popup') {
      return { w: Math.round(popupBox(layout)),
        h: Math.round(Math.min(MD_SHEET_POPUP_H, H - 2 * G)) };
    }
    const W = typeof L.w === 'number' ? L.w : 0;
    /* Full is the screen less the window gutter — except where this shape has
       no second size, and the sheet really is the screen. */
    return laddered(layout)
      ? { w: Math.round(W - 2 * G), h: Math.round(H - 2 * G) }
      : { w: W, h: H };
  }

  /* ── WHERE THE INFO PANEL STANDS (CS-11 · D4) ────────────────────────────
     One component, two placements, and the choice is arithmetic rather than a
     media query: the panel DOCKS when the sheet is wide enough to carry the
     writing measure and the panel side by side, and OVERLAYS when it is not.

     The measure is what is being protected. A docked panel that squeezed the
     column it sits beside would be trading the thing you are doing for a
     description of it — so the test is the honest one, MD_MEASURE plus the
     panel plus the padding the sheet already spends, against the width this
     rung actually gets on this screen. Below that the panel comes over the
     top, where it costs the writing nothing because it is temporary.

     Total, like the ladder: an unknown rung resolves through the ladder first,
     and a missing layout has no room by definition, so it overlays.

     340 is not a taste. `.md-db-row-properties` lays a property out on
     `145px minmax(0,1fr)`, and the panel wears that same rule rather than a
     rhythm of its own — so the panel has to be wide enough to hold it: 145 for
     the label, 18 of padding either side, and 159 left for the value, which is
     a status chip, a project name or a date. Narrower and the shared rule would
     have to be abandoned at exactly the surface that cites it. */
  const MD_INFO_PANEL_W = 340;

  function mdInfoPanelPlacement(size: any, layout: any): string {
    if (!layout) return 'overlay';
    const measure = typeof g.MD_MEASURE === 'number' ? g.MD_MEASURE : 780;
    const { w } = mdSheetSpan(size, layout);
    // 22pt of sheet padding on each side of the writing, which the measure
    // sits inside — the same 22 the frame's gutter uses.
    return w >= measure + MD_INFO_PANEL_W + 2 * MD_SHEET_GUTTER ? 'dock' : 'overlay';
  }

  /** The rectangle, as a style object. Geometry only — the caller dresses it. */
  function mdSheetFrame(size: any, layout: any): Record<string, any> {
    const rung = mdSheetSizeResolve(size, layout);

    /* ── FULL IS A WINDOW (Marwan, 2026-08-21) ──────────────────────────────
       It was `inset: 0` with square corners, while the Projects window standing
       beside it at the very same size kept a 22pt gutter and a rounded edge.
       Two surfaces of the same kind, drawn as though they were not — and the
       sheet was the one that looked wrong, because a rectangle that touches
       every edge reads as the app itself rather than as something laid on it.

       So it keeps the window's rule, which md-databases.jsx states in its own
       words: the board showing around a surface is what says this is over your
       work, and closes. Same gutter, floored by what the device keeps, same
       `--md-radius-sheet`. tests/sheet-frame.test.js compares the two
       rectangles function to function so they cannot drift.

       AND THE WINDOW'S EXCEPTION COMES WITH IT: "where there is no second size
       there is no window either." On a phone, a phone sideways, an iPad held
       sideways, the sheet is the only thing on screen and a gutter around the
       only thing on screen is a margin for nothing. There the glass still
       reaches the corners and the CONTENTS step out of the safe area instead —
       padding on the border box moves every child at once, which is how the
       head clears an iPad's clock without being told. Unfloored on purpose: a
       sheet with no resting gutter has nothing to protect. */
    if (rung === 'full') {
      if (!laddered(layout)) {
        return { inset: 0, borderRadius: 0, boxSizing: 'border-box',
          paddingTop: 'var(--md-safe-top)', paddingBottom: 'var(--md-safe-bottom)',
          paddingInline: 'var(--md-safe-inline)' };
      }
      return {
        insetBlockStart: g_top, insetBlockEnd: g_bottom,
        insetInlineStart: g_inline, insetInlineEnd: g_inline,
        borderRadius: 'var(--md-radius-sheet)',
      };
    }

    /* All four insets, a definite size, and auto margins on both axes: that is
       how an absolutely positioned box centres itself without a transform. A
       transform was the other option and is worse here — it makes the sheet its
       own containing block, which would trap the editor's portalled menus
       inside a rectangle they are supposed to escape. */
    /* The same three expressions popupClear / popupBox / mdSheetPopupStart
       compute, written in CSS so the browser re-evaluates them as the window is
       dragged rather than at the render that happened to measure last. They are
       nested rather than named because a style object has nowhere to put a
       variable; read them against the three functions above, in that order. */
    const day = dockedPanel(layout);
    const reserve = day ? day + MD_SHEET_GUTTER : 0;
    const clear = `max(${g_inline}, min(calc(${g_inline} + ${reserve}px), `
      + `calc(100% - ${g_inline} - ${MD_SHEET_POPUP_MIN}px)))`;
    const box = `min(${MD_SHEET_POPUP_W}px, calc(100% - ${clear} - ${g_inline}))`;
    const start = `max(${clear}, calc((100% - ${box}) / 2))`;
    return {
      insetBlockStart: g_top, insetBlockEnd: g_bottom,
      /* The trailing edge is `auto` ON PURPOSE. With all four insets set and a
         definite size the box is over-constrained, and `margin-inline: auto`
         was what resolved it — by centring in whatever was left AFTER the day,
         which is precisely the rule this stopped following. The leading edge
         now says exactly where the sheet stands, so nothing may be left for a
         margin to decide. The block axis still centres this way, because there
         is nothing on it to clear. */
      insetInlineStart: start, insetInlineEnd: 'auto',
      marginBlock: 'auto', marginInline: 0,
      /* So the number promised IS the number drawn. The caller dresses this
         with a hairline border, and on a content box that border lands OUTSIDE
         inline-size — an 880 popup measured 882 in the engine. Two pixels do
         not matter to the eye; a geometry function whose answer is not what
         appears does, because it is the thing every later comparison trusts. */
      boxSizing: 'border-box',
      inlineSize: box,
      blockSize: `min(${MD_SHEET_POPUP_H}px, calc(100% - ${g_top} - ${g_bottom}))`,
      borderRadius: 'var(--md-radius-sheet)',
    };
  }

  /* ── THE THREE PLANES THIS SHEET OCCUPIES (CS-5) ─────────────────────────
     Not an app-wide ledger — just the sheet's own storey, named once so the
     number is not typed twice and so the next person can see what it is
     ABOVE and what it is UNDER without grepping four files:

         140  Databases, the Workshop         (md-databases.jsx, md-projects.jsx)
         145  the Project window              (md-project-space.jsx)
     →   150  the sheet's scrim
     →   151  the sheet
     →   200  what the sheet ASKS             (MdSheetFrame's `after`)
         300  the editor's own menus          (md-markdown.jsx's MD_CARET_Z)

     A number only means this much once the node is on <body>. The app root is
     `isolation: isolate` (md-app.jsx), which makes it a stacking context, so
     everything inside it is sorted against its siblings and not against the
     windows — which is exactly how a sheet at z 80 ended up BEHIND a window at
     140 while appearing, in the source, to be far below it.

     THE THIRD PLANE IS NEW, AND IT WAS A DEFECT (Marwan, 2026-08-21). A
     confirmation was drawn as a plain sibling of the sheet, and ConfirmDialog
     carries `z-index: 120` of its own — a number chosen long before any of this
     existed, back when the surfaces it interrupted were all inside the app root.
     So "Discard this beginning?" was painted UNDERNEATH the beginning it was
     asking about: the scrim dimmed, nothing appeared, and Escape answered the
     question you could not see. The frame gives it its own storey now, so no
     tenant has to know a number to be heard. */
  const MD_SHEET_SCRIM_Z = 150;
  const MD_SHEET_Z = 151;
  const MD_SHEET_ASK_Z = 200;

  Object.assign(g, {
    MD_SHEET_RUNGS,
    MD_SHEET_GUTTER,
    MD_SHEET_SCRIM_Z,
    MD_SHEET_Z,
    MD_SHEET_ASK_Z,
    MD_SHEET_POPUP_W,
    MD_SHEET_POPUP_H,
    mdDeviceShape,
    mdSheetSizes,
    mdSheetSizeResolve,
    mdSheetStep,
    mdSheetSpan,
    mdSheetPopupStart,
    mdSheetFrame,
    mdSheetBleeds,
    MD_INFO_PANEL_W,
    mdInfoPanelPlacement,
  });
})(typeof window !== 'undefined' ? window : globalThis);
